import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { ChevronLeft } from "lucide-react";
import { PageHeader } from "@/components/app-shell";
import { CalcShell, Field, NumberInput, ResultCard, fmt } from "@/components/calculators/CalcShell";
import { AddToQuoteButton } from "@/components/calculators/AddToQuoteButton";
import { linesFromGips } from "@/components/calculators/lineMappers";
import { gipsCalc } from "@/domain/calculators/carpenter";

export const Route = createFileRoute("/_authenticated/calculations/carpenter/gips")({
  component: GipsPage,
});

function GipsPage() {
  const [area, setArea] = useState<number | "">("");
  const [w, setW] = useState<number | "">(1.20);
  const [h, setH] = useState<number | "">(2.40);
  const [spill, setSpill] = useState<number | "">(10);
  const [layers, setLayers] = useState<number | "">(1);

  const r = useMemo(() => {
    if (area === "" || area <= 0) return null;
    return gipsCalc({
      area_m2: Number(area),
      plate_width_m: w === "" ? undefined : Number(w),
      plate_height_m: h === "" ? undefined : Number(h),
      spill_pct: spill === "" ? undefined : Number(spill),
      layers: layers === "" ? undefined : Number(layers),
    });
  }, [area, w, h, spill, layers]);

  return (
    <>
      <Link to="/calculations" className="mb-4 inline-flex items-center text-sm text-muted-foreground hover:text-foreground">
        <ChevronLeft className="h-4 w-4" /> Beregninger
      </Link>
      <PageHeader title="Gips" subtitle="Beregn antall plater, skruer, sparkel og tape." />
      <CalcShell
        title="Inndata"
        result={r ? (
          <>
            <ResultCard
            title="Materialbehov"
            rows={[
              { label: "Antall plater", value: `${fmt(r.plater, 0)} stk`, strong: true },
              { label: "Skruer",        value: `${fmt(r.skruer, 0)} stk` },
              { label: "Sparkel",       value: `${fmt(r.sparkel_kg, 1)} kg` },
              { label: "Tape",          value: `${fmt(r.tape_m, 0)} m` },
            ]}
            note="Antar ~30 skruer pr plate, 0,35 kg sparkel pr m², 1,5 m tape pr m²."
          />
            <div className="mt-3 flex justify-end">
              <AddToQuoteButton sourceLabel={`Gips`} lines={linesFromGips(r)} defaultLaborHours={Math.max(0, Math.round((Number(area) * 0.35) * 10) / 10)} />
            </div>
          </>
        ) : <div className="card p-5 text-sm text-muted-foreground">Fyll inn areal.</div>}
      >
        <Field label="Areal" unit="m²"><NumberInput value={area} onChange={setArea} /></Field>
        <Field label="Plate­bredde" unit="m"><NumberInput value={w} onChange={setW} /></Field>
        <Field label="Plate­høyde" unit="m"><NumberInput value={h} onChange={setH} /></Field>
        <Field label="Svinn" unit="%"><NumberInput value={spill} onChange={setSpill} /></Field>
        <Field label="Antall lag"><NumberInput value={layers} onChange={setLayers} /></Field>
      </CalcShell>
    </>
  );
}
